<script setup>

import BaseModal from '@/components/Alerts/BaseModal.vue'
import { ButtonSize, ButtonType } from '@/components/Forms/Buttons/SubmitButtonConstants.js'

import { computed, onMounted, ref, watch } from 'vue'
import { FileStatus } from 'filepond'
import { useAuthStore } from '@/stores/authStore.js'
import vueFilePond from 'vue-filepond'
import FilePondPluginFileValidateType from 'filepond-plugin-file-validate-type'
import SubmitButton from '@/components/Forms/Buttons/SubmitButton.vue'
import { Banner, Toaster } from '@/models/utils/Toaster.js';
import { api, ApiGatewayResponse, HttpStatusCode } from '@/api/ApiGateway.js'
import { Tenants } from '@/models/tenants/Tenants'
import { useFileDownload } from '@/composables/useFileDownload'
import { Util } from '@/models/utils/Util.js'
import ActionMenu from '@/components/Forms/ActionMenu.vue'
import UploadResultsModal from '@/views/Admin/User/UploadResultsModal.vue'
import BasicTableViewerModal from '@/components/Alerts/BasicTableViewerModal.vue'
import { echoes } from '@/composables/echo/useEchoes.js'

const props = defineProps({
	show: { type: Boolean, default: false },//Show modal
	modalError: { type: Object, default: () => ({ 'error_eh': false, 'error_msg': 'There was an error loading tags. Please try reloading the page.' }) },
});

const emit = defineEmits(['evCloseModal']);//Used by ManageDocumentsPage to close modal

const { downloadFile } = useFileDownload();

const authStore = useAuthStore();
const toaster = new Toaster();
const banner = new Banner();

const showResultsModal = ref(false);
const uploadResults = ref({'successes': [], 'warnings': [], 'errors': []});
const	validationErrors = ref(null);
const files = ref(null);
const tenant = ref(null);

const tableViewer = ref({ "show": false, "title": "", "data": null, "columns": [] });
const invalidUploadCount = ref(0);

/**
 * Menu Items in top right action menu
 * @type {*[]}
 */
const menuItems = getMenuItems()

//#region Modal Init
/**
 * Update the org count when showing dialog
 */
watch(
	() => props.show,
	(newValue) =>
	{
		if (newValue)
		{
			getInvalidOrgCount();
		}
	}
)

/**
 * Load org count and tenant info
 */
onMounted(() =>
{
	//This loads the org count if the page is refreshed after a code change
	if (props.show)
	{
		getInvalidOrgCount();
	}
	tenant.value = Tenants.getTenant()
})
//#endregion Modal Init

//#region File Upload
const pond = ref(null)
const filesQueued = ref([])
const filesBeingProcessed = ref([])
const fileErrors = ref([])
const uploadProgress = ref(new Map())
const isProcessing = ref(false)

const FilePond = vueFilePond(FilePondPluginFileValidateType)
const serverConfig = {
	url: import.meta.env.VITE_API_URL + '/user-organizations/upload',
	process: (fieldName, file, metadata, load, p_error, progress, abort) => {
		//unused params after abort: transfer, options
		const formData = new FormData()

		const upload_data = {
			"submitter_id": authStore.getAuth.user().id
		}
		formData.append("upload_data", JSON.stringify(upload_data))

		formData.append(fieldName, file, file.name)
		const request = new XMLHttpRequest();
		request.open('POST', import.meta.env.VITE_API_URL + '/user-organizations/upload')
		request.setRequestHeader('X-CSRF-TOKEN', window.csrf_token)
		request.setRequestHeader('Authorization', `Bearer ${useAuthStore().rawJwt}`)
		request.setRequestHeader('x-client-user-id', authStore.getAuth?.user()?.id)
		request.setRequestHeader('x-jwt-version-code', authStore.getAuth?.user()?.jwt_version_code)
		request.setRequestHeader('X-Socket-ID', echoes.socketId());

		request.upload.onprogress = (e) => {
			progress(e.lengthComputable, e.loaded, e.total)
		}

		request.onload = function () {
			if (request.status >= 200 && request.status < 300) {
				load(request.responseText)
			}
			else
			{
				const resp = JSON.parse(request.response)
				if (resp.errors && (resp.errors.length > 0)) {
					validationErrors.value = new ApiGatewayResponse({
						'status': HttpStatusCode.ValidationError,
						'response': {
							'data': {
								'errors': resp.errors
							}
						}
					}, JSON.parse(request.response))
				}


				p_error('File upload failed.')
			}
		}

		request.send(formData)

		return {
			abort: () => {
				request.abort()
				abort()
			},
		};
	},
}

const uploadEnabled = computed(() => {
	return (isProcessing.value === false && filesQueued.value != null && filesQueued.value.length > 0)
})

function onFileAdded(p_error, file) {
	const outerContainer = document.getElementById('filepond--item-' + file.id)
	let infoMain = outerContainer.querySelector('.filepond--file-info-main')

	if (infoMain) {
		const wrapper = document.createElement('div')
		wrapper.classList.add('flex', 'space-x-2', 'filepond--file-info-main', 'w-full')
		infoMain.parentNode.insertBefore(wrapper, infoMain)
		infoMain.classList.remove('filepond--file-info-main')
		wrapper.appendChild(infoMain)

		const spinnerId = 'file-exists-spinner-' + file.id
		const warningId = 'file-exists-warning-' + file.id
		const fileExistsWrapper = document.createElement('div')
		fileExistsWrapper.classList.add('truncate')
		fileExistsWrapper.innerHTML = `
			<svg id="` + spinnerId + `" role="img" aria-labelledby="spinnerTitle" class="pointer-events-auto animate-spin h-4 w-4 text-primary" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
				<title id="spinnerTitle">Checking for file name conflict...</title>
				<circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2"></circle>
				<path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
			</svg>
			<span id="` + warningId + `" class="hidden text-error pointer-events-auto" style="font-size: 14px;" title="A file with this name already exists. After uploading, please provide a description to avoid confusion.">A file with this name already exists. After uploading, please provide a description to avoid confusion.</span>
		`
		wrapper.appendChild(fileExistsWrapper)

		document.getElementById(spinnerId).classList.add("hidden")

	}
}

function onUpdateFiles(files) {
	filesBeingProcessed.value = []

	// Files about to begin processing
	filesBeingProcessed.value = []
	let filtered = files.filter((file) => {
		return file.status === FileStatus.PROCESSING_QUEUED
	})

	filtered.forEach((file) => {
		filesBeingProcessed.value.push(file)
	})

	// Files queued waiting for processing to start
	filesQueued.value = []
	filtered = files.filter((file) => {
		return file.status === FileStatus.IDLE
	})

	filtered.forEach((file) => {
		filesQueued.value.push(file)
	})

	isProcessing.value = filesBeingProcessed.value.length > 0
}

function onFileProcessed(p_error, p_file)
{
	const response_data = JSON.parse(p_file.serverId)
	console.log('onFileProcessed', p_error, p_file)
	handleUploadResponse(response_data, p_error)
	if (p_error) fileErrors.value.push({ "error": p_error, "file": { "name": p_file.filename, "status": p_file.status } })

	if (isProcessingComplete())
	{
		onFilesProcessed()
	}
}

function handleUploadResponse(p_data, p_error)
{
	if (authStore.isDeveloper) console.log("handleUploadResponse", p_data, p_error)

	uploadResults.value = {
		'successes': [],
		'warnings': [],
		'errors': []
	}

	//Columns to show in error results
	const columns = [
		{ title: 'Email', key: 'email' },
		{ title: 'Organization', key: 'organization' }
	]

	//Update the count
	getInvalidOrgCount()

	//Collect the different error results
	if (Util.objHasArray(p_data, 'added_org'))
	{
		uploadResults.value.successes.push({'type': 'table', 'columns': columns, 'heading': 'Organizations Added', 'items':  p_data['added_org']})
	}

	if (Util.objHasArray(p_data, 'excel_validation'))
	{
		uploadResults.value.errors.push({'heading': 'Validation Errors (not imported)', 'items':  p_data['excel_validation']})
	}

	if (Util.hasProp(p_data, 'invalid_count'))
	{
		uploadResults.value.errors.push({'heading': 'Total Invalid Records', 'items':  [{ id: 1, text: p_data['invalid_count'] }]})
	}

	if (Util.objHasArray(p_data, 'org_exists_records'))
	{
		uploadResults.value.warnings.push({'type': 'table', 'columns': columns, 'heading': 'User has Organization', 'items':  p_data['org_exists_records']})
	}

	if (Util.objHasArray(p_data, 'invalid_email_org'))
	{
		uploadResults.value.errors.push({ 'type': 'table', 'columns': columns, 'heading': 'User and Organization not found', 'items':  p_data['invalid_email_org']})
	}

	if (Util.objHasArray(p_data, 'invalid_email'))
	{
		uploadResults.value.errors.push({'type': 'table', 'columns': columns, 'heading': 'User Not Found', 'items':  p_data['invalid_email']})
	}

	if (Util.objHasArray(p_data, 'invalid_org'))
	{
		uploadResults.value.errors.push({'type': 'table', 'columns': columns, 'heading': 'Invalid or Disabled Organization', 'items':  p_data['invalid_org']})
	}

	if (Util.objHasArray(p_data, 'invalid_role'))
	{
		const role_columns = [
			{ title: 'Email', key: 'email' },
			{ title: 'Role ID', key: 'organization_role_id' }
		]

		uploadResults.value.errors.push({'type': 'table', 'columns': role_columns, 'heading': 'Invalid Role', 'items':  p_data['invalid_role']})
	}

	if (Util.objHasArray(p_data, 'failed_records'))
	{
		uploadResults.value.errors.push({'type': 'table', 'columns': columns, 'heading': 'Unknown Failure', 'items':  p_data['failed_records']})
	}

	if (authStore.isDeveloper && Util.objHasArray(p_data, 'exceptions'))
	{
		uploadResults.value.errors.push({'heading': 'Exceptions', 'items':  p_data['exceptions']})
	}

	if (uploadResults.value.successes.length > 0 || uploadResults.value.errors.length > 0 || uploadResults.value.warnings.length > 0)
	{
		pond.value.removeFiles();
		showResultsModal.value = true;
	}

}

/**
 * This is redundant in general, but in the case of a file that failed to upload retrying doesn't trigger onUpdateFiles()
 */
function onFileProcessStart(file) {
	const fileAlreadyThere = filesBeingProcessed.value.find((item) => {
		return item.id === file.id
	})

	if (!fileAlreadyThere) {
		fileErrors.value = []
		filesBeingProcessed.value.push(file)
	}

}

/**
 * This was getting called once for every time there was a failed upload (like no doc name) which could cause
 * multiple Success toasts once successful. Removed from the file-pond component and called only when the file is successful.
 */
function onFilesProcessed()
{
	isProcessing.value = false

	filesBeingProcessed.value = []
	uploadProgress.value = new Map()

	if (fileErrors.value.length === 0)
	{
		filesQueued.value = []
		isProcessing.value = false
		//dismissModal(true)
	}
	else
	{
		console.log('toasty', fileErrors.value, errorDetails().join('. '))
		toaster.error(errorDetails().join('. '))
	}
}

/**
 * Extracts the error details
 * @returns {string[]} Array of error strings.
 */
function errorDetails()
{
	const errors = []

	const rawErrors = validationErrors.value.error?.response?.data?.errors ?? []

	for (const i in rawErrors)
	{
		const error = rawErrors[i]
		if (Object.prototype.hasOwnProperty.call(error, 'detail'))
		{
			errors.push(error['detail'])
		}
	}
	if (errors.length <= 0)
	{
		errors.push('An unknown error was encountered.')
	}

	return errors
}

function onProcessFileProgress(file, progress) {
	uploadProgress.value.set(file.id, progress)
}

function onError()
{
	//unused params p_error, p_file, p_status
	isProcessing.value = false
}

/**
 * If one or more of the files have an error when uploading, the onFilesProcessed() function does not get called and isProcessing doesn't get updated to true
 */
function isProcessingComplete() {
	const nonErrorStatuses = [FileStatus.IDLE, FileStatus.LOADING, FileStatus.PROCESSING_QUEUED, FileStatus.PROCESSING, FileStatus.PROCESSING_COMPLETE]

	//console.log('isProcessingComplete.filesBeingProcessed', filesBeingProcessed)
	const filesProcessingOrComplete = filesBeingProcessed.value.filter(file => {
		return file.status in nonErrorStatuses
	})

	return filesProcessingOrComplete.length === 0
}

/** Uploads spreadsheet */
function beginUpload() {
	fileErrors.value = []
	isProcessing.value = true
	pond.value.processFiles()
}

//#endregion File Upload

//#region Misc Org Functions
/**
 * Retry adding orgs to user from a previous import.
 */
function retryAddingOrgs()
{
	const upload_data = {
		"submitter_id": authStore.getAuth.user().id
	}

	isProcessing.value = true
	api.post('user-organizations/user-organizations/retry-imports', {"upload_data": upload_data}).then(response =>
	{
		isProcessing.value = false

		//Update count
		getInvalidOrgCount()

		if (authStore.isPortalAdmin) console.log('Retry response: ', response)

		if (response.hasError())
		{
			toaster.error('Error retrying adding user organizations.')
			return
		}

		if (response?.response?.data)
		{
			handleUploadResponse(response?.response?.data)
		}
		else
		{
			console.log('retryAddingOrgs response: ', response)
			toaster.warning("Unexpected response resending invites.")
		}
	})
}

/**
 * Clear all user organization uploads.
 */
function clearOrgUploads()
{
	const upload_data = {
		"submitter_id": authStore.getAuth.user().id
	}

	isProcessing.value = true
	api.post('user-organizations/user-organizations/clear-imports', {"upload_data": upload_data}).then(response =>
	{
		isProcessing.value = false

		//Update count
		getInvalidOrgCount()

		if (authStore.isPortalAdmin) console.log('Clear imports response: ', response)

		if (response.hasError())
		{
			toaster.error('Error deleting user organization import records.')
			return
		}

		const resp_data = response?.response?.data;

		if (!Util.isEmpty(resp_data['result']))
		{
			const error_eh = resp_data.result['error'];
			const recs_deleted = resp_data.result['records_deleted'];

			if (error_eh) toaster.error('There was an error deleting imported records.');
			else if (recs_deleted > 0)toaster.success("Imported records deleted: " + recs_deleted);
			else toaster.info("There were no records to delete.");

			//handleClearResponse(response?.response?.data)
		}
		else
		{
			console.log('Clear Imports response: ', response)
			toaster.warning("Unexpected response clearing imports.")
		}
	})
}

/**
 * Get a count of all invalid records.
 */
function getInvalidOrgCount()
{
	isProcessing.value = true
	api.get('user-organizations/user-organizations/invalid-upload-count').then(response =>
	{
		isProcessing.value = false
		if (authStore.isPortalAdmin) console.log('getInvalidOrgCount response: ', response)

		if (response.hasError())
		{
			toaster.error('Error getting invalid user organization upload count..')
			return
		}

		const resp_data = response?.response?.data;
		if (Util.hasProp(resp_data, 'invalid_count'))
		{
			invalidUploadCount.value = resp_data['invalid_count'];

			menuItems[1].text = "Retry Adding Orgs (" + invalidUploadCount.value + ")";
			console.log('invalid_count:', invalidUploadCount.value);
			//handleClearResponse(response?.response?.data)
		}
		else
		{
			console.log('Count response: ', response)
			toaster.warning("Unexpected response getting invalid user organization upload count.")
		}
	})
}

/**
 * Returns a list of all the invalid records
 */
function viewInvalidUploads()
{
	isProcessing.value = true
	api.get('user-organizations/user-organizations/invalid-uploads').then(response =>
	{
		isProcessing.value = false
		if (authStore.isPortalAdmin) console.log('viewInvalidUploads response: ', response)

		if (response.hasError())
		{
			toaster.error('Error getting invalid user organization uploads..')
			return
		}

		const resp_data = response?.response?.data;
		if (Util.hasProp(resp_data, 'invalid_records'))
		{
			const invalid_records = resp_data['invalid_records'];

			if (invalid_records.length > 0)
			{
				toggleTableViewerModal(true, "Invalid Records", invalid_records);
			}
			else
			{
				toaster.info("There are no invalid records.");
			}

			console.log('invalid_records:', invalid_records);
			//handleClearResponse(response?.response?.data)
		}
		else
		{
			console.log('Count response: ', response)
			toaster.warning("Unexpected response getting invalid user organization uploads.")
		}
	})
}
//#endregion Misc Org Functions

//#region Modal
/**
 * Check if there's any changes, if not, proceed with closing the modal
 * @param p_reload_eh True to reload the upload grid
 */
function dismissModal(p_reload_eh)
{
		closeModal(p_reload_eh)
}

/**
 * Do cleanup, show success toast and tell the UploadDocumentPage to close the modal
 * @param p_reload_eh
 */
function closeModal(p_reload_eh)
{
	cleanupModal()
	//if (p_reload_eh) toaster.success("Your document has been successfully added.")
	emit('evCloseModal', 'user_org_upload', p_reload_eh)
}

/**
 * Tidy up
 */
function cleanupModal()
{
	validationErrors.value = null
	fileErrors.value = []
	filesQueued.value = []
	isProcessing.value = false
}

/**
 * Shows or hides the Table Viewer Modal
 * @param {boolean} p_show True to show modal
 * @param {string} p_title Title of modal
 * @param p_data
 */
function toggleTableViewerModal(p_show, p_title, p_data)
{
	if (p_show)
	{
		tableViewer.value.show = true;
		tableViewer.value.data = p_data;
		tableViewer.value.title = p_title + " (" + invalidUploadCount.value + ")";
		tableViewer.value.columns = [
			{ title: 'Email', key: 'email' },
			{ title: 'Organization', key: 'organization', width: '180px' },
			{ title: 'Status', key: 'status_message' },
		];
	}
	else
	{
		tableViewer.value.show = false;
		tableViewer.value.data = null;
		tableViewer.value.title = "";
	}
}
//#endregion Modal

//#region Downloading
async function downloadTemplate()
{
	isProcessing.value = true
	toaster.info('Requesting download...', { timeout: false })

	await downloadFile(
		'user-organizations/user-organizations/download-template',
		null,
		'user-org-template-' + Util.today('-', 'YYYY-MM-DD') + '.xlsx',
		onDownloadSuccess,
		onDownloadError)
}

function onDownloadSuccess(p_response)
{
	toaster.clear();
	isProcessing.value = false
	if (!p_response)
	{
		toaster.error('Template failed to download.')
	}
}

function onDownloadError(_p_err, p_errs, p_auth_error)
{
	toaster.clear()
	isProcessing.value = false
	try
	{
		const devEnvEh = Tenants.isDevHost() && authStore.isPortalAdmin;
		toaster.clear()
		if (p_errs.length > 0)
		{
			const err_msgs = p_errs.map(p_item => p_item.detail);
			if (devEnvEh)
			{
				banner.error("Dev Only Banner:<br>" + err_msgs.join('<br/>'));
			}

			const toast_msg = p_auth_error ? "You do not have permission to download this template." : "Template failed to download.";
			toaster.error(toast_msg);
		}
		else
		{
			toaster.error('Template failed to download.')
		}
	}
	catch(e)
	{
		console.error(e)
	}
}
//#endregion Downloading

//#region UI
function getMenuItems()
{
	const items = [];
	Util.addActionMenuItem(items, 'template', 'Download Template');
	Util.addActionMenuItem(items, 'retry', 'Retry Adding Orgs');
	Util.addActionMenuItem(items, 'clear', 'Clear Previous Uploads');
	Util.addActionMenuItem(items, 'view', 'View Invalid Uploads');

	return items
}

function onActionMenuItemSelected(p_menu_item)
{
	switch (p_menu_item.name)
	{
		case "template":
			downloadTemplate()
			break;
		case "retry":
			retryAddingOrgs();
			break;
		case "clear":
			clearOrgUploads();
			break;
		case "view":
			viewInvalidUploads();
			break;
		default:
			alert("Not yet implemented")
			break;
	}
}

//#endregion UI

</script>

<template>
	<div>
		<BaseModal :show="show" border-classes="border-t-6 border-tertiary">
			<div class="w-136 h-192">
				<div class="flex justify-between items-center border-b border-neutral w-full pb-4">
					<p class="flex-grow text-xl font-bold text-heading">Upload User Organizations</p>
					<ActionMenu
						:items="menuItems"
						@onActionMenuItemSelected="onActionMenuItemSelected"
						placement="bottom-end"
					></ActionMenu>

					<img src="@/assets/images/icon/dismiss.svg" alt="Dismiss" title="Dismiss" class="cursor-pointer size-4" @click.prevent="dismissModal(false)">
				</div>

				<div v-if="props.modalError.error_eh">
					<div class="w-130 mt-6 text-error font-bold">{{ props.modalError.error_msg }}</div>
				</div>

				<div class="text-label mt-2">
					Use upper right menu to download upload template file, retry adding previous uploaded organizations to users or clear previous uploads.
					<br/>
					<br/>
					Please ensure you are using the latest version of the template before proceeding.
				</div>

				<form>
					<div class="flex flex-col mt-2">

						<label class="text-label mt-5 mb-2">Upload</label>
						<div class="border border-neutral rounded ">
							<!--
								allow-process: false removes retry button if fails
								labelTapToRetry, labelTapToCancel: set to blank cause disabling retry doesn't hide it for some reason  -->
							<file-pond
								class="h-30"
								name="file"
								ref="pond"
								v-bind:files="files"
								label-idle="Drop Excel file here or <span class='filepond--label-action'>Browse</span>"
								:allow-multiple="false"
								:max-files="1"
								:server="serverConfig"
								:allow-process="false"
								labelTapToRetry=""
								labelTapToCancel=""
								:allow-revert="false"
								:allow-remove="true"
								:instant-upload="false"
								:max-parallel-uploads="1"
								accepted-file-types="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"

								@addfile="onFileAdded"
								@processfilestart="onFileProcessStart"
								@processfile="onFileProcessed"
								@updatefiles="onUpdateFiles"
								@processfileprogress="onProcessFileProgress"
								@error="onError"
							/>
						</div>
					</div>
					<!-- Save/Cancel -->
					<div class="flex w-full flex-col-reverse sm:flex-row mt-4 sm:gap-y-1 gap-y-1">

						<div class="sm:mr-auto"></div>

						<SubmitButton label="Cancel" :type="ButtonType.Outline" :size="ButtonSize.Small" @click.prevent="dismissModal(false)"
						              class="sm:mr-2" />
						<SubmitButton label="Upload" :size="ButtonSize.Small" @click.prevent="beginUpload" :disabled="!uploadEnabled" :loading="isProcessing" />

					</div>
				</form>
			</div>
		</BaseModal>
	</div>

	<Teleport to="html">
		<UploadResultsModal :show="showResultsModal" :results="uploadResults" @evCloseModal="showResultsModal = false" />
	</Teleport>

	<Teleport to="html">
		<BasicTableViewerModal
			:data="tableViewer.data"
			:columns="tableViewer.columns"
			:show="tableViewer.show"
			:title="tableViewer.title"
			@close="toggleTableViewerModal(false)"
		/>
	</Teleport>

</template>

<style scoped>
/* deep needed for these styles to work with 'scoped' */
:deep(.filepond--panel)
{
	display:none !important;
}

:deep(.filepond--root .filepond--drop-label) {
	width: 100%;
	height: fit-content !important;
	transform: translate3d(0, 0, 0) !important;
	color: #0a0a0a;
	text-align: center;
	font-size: 16px;
	font-style: normal;
	font-weight: 400;
	line-height: 1.5;
	padding: 3em 0em !important;

	label {
		width: 100% !important;
	}
}

:deep(.filepond--list) {
	.filepond--item {
		background-color: #EFF6FB !important;
		@apply border border-disabled rounded-md;
	}

	.filepond--file, .filepond--file-info-sub, .filepond--file-status-main, .filepond--file-status-sub {
		color: #11181D !important;
		@apply font-normal !important;
		opacity: 1;
	}

	.filepond--file-action-button:hover, .filepond--file-action-button:focus {
		@apply !ring-2 !ring-primary-darkest/65;
	}
}

</style>